/**
 * Handler: view_item event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { trackKlaviyoViewItemDirect } from '../fallbacks/klaviyo';
import { sendGA4Fallback, mapGA4Item } from '../fallbacks/ga4';
import { gtmMarketFields } from '../utils';

export function handleViewItem(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  variantId?: string
): void {
  const { product: adaptedProduct, variant } = adaptProduct(product, variantId);
  const varId = variant.id.toString();

  // GA4 - CDN's gaViewItem is product-page gated, fall back to direct gtag
  if (typeof analyzify.gaViewItem === 'function') {
    analyzify.gaViewItem({ product: adaptedProduct, variant }, varId);
  } else {
    const collection = (window as any).analyzify?.collection;
    sendGA4Fallback(analyzify, 'view_item', {
      currency: analyzify.currency || 'USD',
      value: analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100,
      items: [mapGA4Item(analyzify, adaptedProduct, variant, 0, collection?.id, collection?.title)],
    });
  }

  // Facebook
  if (typeof analyzify.fbViewContent === 'function') {
    analyzify.fbViewContent({ product: adaptedProduct, variant }, varId);
  }

  // TikTok
  if (typeof analyzify.ttViewContent === 'function') {
    analyzify.ttViewContent({ product: adaptedProduct, variant });
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  const win = window as any;
  let gtmTracked = false;
  if (typeof analyzify.gtmViewItem === 'function') {
    try {
      analyzify.gtmViewItem({ product: adaptedProduct, variant }, varId);
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && analyzify.properties?.GTM?.status) {
    const collection = win.analyzify?.collection;
    const variantPrice = analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100;
    const compareAtPrice = variant.compare_at_price ? (analyzify.formatPrice?.(variant.compare_at_price, true) || 0) : 0;
    const actualPrice = adaptedProduct.price ? (analyzify.formatPrice?.(adaptedProduct.price, true) ?? adaptedProduct.price / 100) : variantPrice;
    const getDiscount = (compareAtPrice > 0 && compareAtPrice !== actualPrice) ? Math.abs(compareAtPrice - actualPrice) : 0;

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_view_item',
      ss_event_id: analyzify.eventId.get('view_item') || null,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        currency: analyzify.currency || null,
        value: analyzify.formatPrice?.(variant.price, true) ?? variantPrice,
        cart_id: analyzify.cart_id || null,
        items: [{
          id: analyzify.getShopifyId?.(
            analyzify.feed_region || '',
            adaptedProduct.id,
            variant.id,
            variant.sku
          ) || null,
          item_id: adaptedProduct.id?.toString() || null,
          item_name: adaptedProduct.title?.trim() || null,
          item_list_name: collection?.title?.trim() || null,
          item_list_id: collection?.id || null,
          item_list_handle: collection?.handle || null,
          price: actualPrice,
          item_category: adaptedProduct.type || null,
          item_brand: adaptedProduct.vendor || null,
          item_barcode: variant.barcode || null,
          discount: analyzify.formatPrice?.(getDiscount, false) || 0,
          item_variant: variant.title || null,
          item_variant_id: varId?.toString() || variant.id?.toString() || null,
          item_sku: variant.sku || null,
          quantity: 1,
        }],
      },
      ...gtmMarketFields(analyzify),
    });
  }

  // Bing
  if (typeof analyzify.bingViewItem === 'function') {
    analyzify.bingViewItem({ product: adaptedProduct, variant });
  }

  // Pinterest
  if (typeof analyzify.pinterestViewItem === 'function') {
    analyzify.pinterestViewItem({ product: adaptedProduct, variant });
  }

  // Criteo
  if (typeof analyzify.criteoViewProduct === 'function') {
    analyzify.criteoViewProduct({ product: adaptedProduct, variant });
  }

  // Klaviyo - try CDN first, fall back to direct API
  const klaviyoPropsVI = analyzify.properties?.KLAVIYO;
  if (klaviyoPropsVI?.status && klaviyoPropsVI.events?.view_item) {
    if (typeof analyzify.klaviyoViewItem === 'function') {
      try {
        analyzify.klaviyoViewItem({ product: adaptedProduct, variant });
      } catch (error) {
        trackKlaviyoViewItemDirect(analyzify, adaptedProduct, variant);
      }
    } else {
      trackKlaviyoViewItemDirect(analyzify, adaptedProduct, variant);
    }
  }

  // Snapchat
  if (typeof analyzify.snapchatViewContent === 'function') {
    analyzify.snapchatViewContent({ product: adaptedProduct, variant });
  }

  // Server-side
  if (typeof analyzify.ssViewItem === 'function') {
    analyzify.ssViewItem({ product: adaptedProduct, variant }, varId);
  }
}
